import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Trash2, FileText, Palette, ExternalLink, Eye, EyeOff, Sparkles, Loader2, Copy, Check, UploadCloud, Rocket, RefreshCw, Plus, X as XIcon, BookOpen, FolderOpen, Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { api, errMsg, errCode, fmtRel, toDateInput, fromDateInput } from "@/lib/api";
import { useMe } from "@/lib/me";
import { STORY_STAGES, FORMATS, SECTIONS, canAccess, WEBFLOW_STATUS } from "@/lib/constants";
import { Pill, WebflowBadge, SampleTag } from "@/components/app/shared";
import { cn } from "@/lib/utils";
import { WebflowMetaSection, WebflowExtraSections, WF_STORY_KEYS, useWebflowSchema, useWebflowRefs, previewValue, AUTO_FILLED } from "@/components/editorial/WebflowFields";
import DrivePicker from "@/components/google/DrivePicker";
import ImageUploader from "@/components/ImageUploader";
import TasksWidget from "@/components/google/TasksWidget";

const Field = ({ label, children, hint }) => (
  <div className="grid gap-1.5">
    <div className="flex items-baseline justify-between">
      <Label className="text-xs">{label}</Label>
      {hint}
    </div>
    {children}
  </div>
);

const Counter = ({ value = "", max }) => (
  <span className={cn("font-mono text-[10.5px]", value.length > max ? "text-bad" : "text-muted-foreground")}>{value.length}/{max}</span>
);

const docsEmbed = (u) => (u.includes("docs.google.com") ? u.replace(/\/edit.*$/, "/preview") : u);
const canvaEmbed = (u) => (u.includes("canva.com") && !u.includes("embed") ? `${u.split("?")[0].replace(/\/edit$/, "/view")}?embed` : u);

function AssetLink({ label, icon: Icon, value, onChange, disabled, embedFn, testid, placeholder }) {
  const [show, setShow] = useState(false);
  const valid = /^https?:\/\//.test(value || "");
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="flex items-center gap-2 text-xs font-medium"><Icon className="h-3.5 w-3.5" strokeWidth={1.75} /> {label}</div>
      <div className="mt-2 flex gap-1.5">
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} placeholder={placeholder} className="h-8 font-mono text-[11px]" data-testid={`${testid}-input`} />
        <Button variant="outline" size="icon" className="h-8 w-8 shrink-0" disabled={!valid} asChild={valid} aria-label="Abrir">
          {valid ? <a href={value} target="_blank" rel="noreferrer" data-testid={`${testid}-open`}><ExternalLink className="h-3.5 w-3.5" /></a> : <span><ExternalLink className="h-3.5 w-3.5" /></span>}
        </Button>
        <Button variant="outline" size="icon" className="h-8 w-8 shrink-0" disabled={!valid} onClick={() => setShow(!show)} data-testid={`${testid}-embed`} aria-label="Incrustar">
          {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
        </Button>
      </div>
      {show && valid && (
        <div className="mt-2 overflow-hidden rounded-md border border-border bg-background">
          <iframe title={label} src={embedFn(value)} className="h-[420px] w-full" allowFullScreen />
          <div className="border-t border-border px-2 py-1 text-[10.5px] text-muted-foreground">Si no carga, el documento debe estar compartido con "cualquiera con el enlace".</div>
        </div>
      )}
    </div>
  );
}

function CopyBtn({ text }) {
  const [ok, setOk] = useState(false);
  return (
    <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Copiar" data-testid="copy-button"
      onClick={() => { navigator.clipboard?.writeText(text || ""); setOk(true); setTimeout(() => setOk(false), 1200); }}>
      {ok ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    </Button>
  );
}

function ListEditor({ items = [], onChange, max, disabled, testid, placeholder }) {
  return (
    <div className="space-y-2" data-testid={testid}>
      {items.map((t, i) => (
        <div key={i} className="flex gap-2">
          <span className="mt-2 w-5 shrink-0 font-mono text-[10.5px] text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
          <div className="flex-1">
            <Textarea rows={2} value={t} disabled={disabled} onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))} className="min-h-[56px] text-[13px]" />
            {max && <div className="mt-0.5 text-right"><Counter value={t} max={max} /></div>}
          </div>
          {!disabled && (
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onChange(items.filter((_, j) => j !== i))} aria-label="Quitar"><XIcon className="h-3.5 w-3.5" /></Button>
          )}
        </div>
      ))}
      {!disabled && (
        <Button variant="ghost" size="sm" onClick={() => onChange([...items, ""])}><Plus className="mr-1 h-3.5 w-3.5" /> {placeholder || "Agregar"}</Button>
      )}
    </div>
  );
}

function SocialBlock({ title, sub, validated, onValidate, disabled, children, testid }) {
  return (
    <section className="rounded-lg border border-border bg-card" data-testid={testid}>
      <div className="flex items-center justify-between border-b border-border px-5 py-3">
        <div>
          <div className="text-sm font-semibold">{title}</div>
          <div className="text-[11px] text-muted-foreground">{sub}</div>
        </div>
        <label className="flex items-center gap-2 text-xs">
          <span className={validated ? "text-ok" : "text-muted-foreground"}>{validated ? "validado" : "sin validar"}</span>
          <Switch checked={!!validated} onCheckedChange={onValidate} disabled={disabled} data-testid={`${testid}-validate`} />
        </label>
      </div>
      <div className="space-y-4 p-5">{children}</div>
    </section>
  );
}

function SocialPack({ story, canEdit, pub, onSaved }) {
  const qc = useQueryClient();
  const [pack, setPack] = useState(story.socialPack);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setPack(story.socialPack); setDirty(false); }, [story.socialPack]);
  const gen = useMutation({
    mutationFn: async () => (await api.post(`/stories/${story.id}/social-pack`)).data,
    onSuccess: (p) => { setPack(p); setDirty(false); qc.invalidateQueries({ queryKey: ["story", story.id] }); toast.success("Social Pack generado"); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const save = useMutation({
    mutationFn: async () => (await api.patch(`/stories/${story.id}`, { socialPack: pack })).data,
    onSuccess: (s) => { setDirty(false); onSaved(s); toast.success("Social Pack guardado"); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const upd = (path, value) => {
    setDirty(true);
    setPack((p) => {
      const n = JSON.parse(JSON.stringify(p));
      const [a, b] = path.split(".");
      n[a][b] = value;
      return n;
    });
  };
  const canvaUrl = story.canvaUrl || pub?.canvaTemplateUrl || "https://www.canva.com/";
  const dis = !canEdit;

  if (!pack)
    return (
      <div className="grain rounded-lg border border-dashed border-border bg-surface-2 px-6 py-14 text-center" data-testid="social-pack-empty">
        <div className="font-display text-4xl">Social Pack</div>
        <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">Genera en español el hook y el hilo para X, el caption y carrusel para Instagram/Facebook y el guion para TikTok/Shorts a partir de esta historia.</p>
        {pub && !pub.aiConfigured ? (
          <p className="mt-5 text-sm">Falta configurar la clave de IA en <Link className="underline" to="/integraciones">Webflow & Integraciones</Link>.</p>
        ) : (
          canEdit && (
            <Button className="mt-5" onClick={() => gen.mutate()} disabled={gen.isPending} data-testid="social-pack-generate-button">
              {gen.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />}
              {gen.isPending ? "Generando…" : "Generar Social Pack"}
            </Button>
          )
        )}
      </div>
    );

  const v = pack.validated || {};
  const allOk = v.x && v.instagram && v.tiktok;
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Pill><span className={cn("h-1.5 w-1.5 rounded-full", allOk ? "dot-ok" : "dot-warn")} />{allOk ? "Paquete validado para distribución" : "Pendiente de validación"}</Pill>
          {pack.generatedAt && <span>Generado {fmtRel(pack.generatedAt)}</span>}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <a href={canvaUrl} target="_blank" rel="noreferrer" data-testid="open-canva-button"><Palette className="mr-1.5 h-4 w-4" /> Abrir en Canva</a>
          </Button>
          {canEdit && (
            <>
              <Button variant="outline" onClick={() => gen.mutate()} disabled={gen.isPending} data-testid="social-pack-regenerate-button">
                {gen.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />} Regenerar
              </Button>
              <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending} data-testid="social-pack-save-button"><Save className="mr-1.5 h-4 w-4" /> Guardar</Button>
            </>
          )}
        </div>
      </div>
      <SocialBlock testid="social-x" title="X" sub="Hook de 280 caracteres + esquema de hilo" validated={v.x} disabled={dis} onValidate={(val) => upd("validated.x", val)}>
        <Field label="Hook" hint={<div className="flex items-center gap-1"><Counter value={pack.x.hook} max={280} /><CopyBtn text={pack.x.hook} /></div>}>
          <Textarea rows={3} value={pack.x.hook} disabled={dis} onChange={(e) => upd("x.hook", e.target.value)} data-testid="social-x-hook" />
        </Field>
        <Field label="Hilo"><ListEditor items={pack.x.thread} max={280} disabled={dis} onChange={(val) => upd("x.thread", val)} testid="social-x-thread" placeholder="Agregar tweet" /></Field>
      </SocialBlock>
      <SocialBlock testid="social-instagram" title="Instagram & Facebook" sub="Caption editorial, secuencia de carrusel y créditos" validated={v.instagram} disabled={dis} onValidate={(val) => upd("validated.instagram", val)}>
        <Field label="Caption" hint={<div className="flex items-center gap-1"><Counter value={pack.instagram.caption} max={2200} /><CopyBtn text={`${pack.instagram.caption}\n\n${(pack.instagram.hashtags || []).join(" ")}`} /></div>}>
          <Textarea rows={6} value={pack.instagram.caption} disabled={dis} onChange={(e) => upd("instagram.caption", e.target.value)} data-testid="social-ig-caption" />
        </Field>
        <Field label="Carrusel"><ListEditor items={pack.instagram.carousel} disabled={dis} onChange={(val) => upd("instagram.carousel", val)} testid="social-ig-carousel" placeholder="Agregar slide" /></Field>
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Créditos"><Textarea rows={2} value={pack.instagram.credits} disabled={dis} onChange={(e) => upd("instagram.credits", e.target.value)} /></Field>
          <Field label="Hashtags"><Input value={(pack.instagram.hashtags || []).join(" ")} disabled={dis} onChange={(e) => upd("instagram.hashtags", e.target.value.split(/\s+/).filter(Boolean))} className="font-mono text-xs" /></Field>
        </div>
      </SocialBlock>
      <SocialBlock testid="social-tiktok" title="TikTok & YouTube Shorts" sub="Gancho visual de 3 segundos, voz en off y escenas" validated={v.tiktok} disabled={dis} onValidate={(val) => upd("validated.tiktok", val)}>
        <Field label="Gancho (0–3 s)"><Textarea rows={2} value={pack.tiktok.hook} disabled={dis} onChange={(e) => upd("tiktok.hook", e.target.value)} data-testid="social-tiktok-hook" /></Field>
        <Field label="Guion de voz en off" hint={<CopyBtn text={pack.tiktok.voiceover} />}><Textarea rows={5} value={pack.tiktok.voiceover} disabled={dis} onChange={(e) => upd("tiktok.voiceover", e.target.value)} /></Field>
        <Field label="Escenas"><ListEditor items={pack.tiktok.scenes} disabled={dis} onChange={(val) => upd("tiktok.scenes", val)} testid="social-tiktok-scenes" placeholder="Agregar escena" /></Field>
      </SocialBlock>
    </div>
  );
}

function WebflowTab({ story, canEdit, pub, me, onSaved }) {
  const [confirm, setConfirm] = useState(false);
  const push = useMutation({
    mutationFn: async (live) => (await api.post(`/stories/${story.id}/webflow`, { live })).data,
    onSuccess: (s, live) => { onSaved(s); toast.success(live ? "Publicado en Webflow" : "Enviado a Webflow como borrador"); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const check = useMutation({
    mutationFn: async () => (await api.get(`/stories/${story.id}/webflow/status`)).data,
    onSuccess: (r) => { onSaved({ ...story, webflowStatus: r.webflowStatus, webflowItemId: r.webflowItemId === undefined ? story.webflowItemId : r.webflowItemId }); toast.success(`Estado: ${WEBFLOW_STATUS[r.webflowStatus]}`); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const configured = pub?.webflowConfigured;
  const { data: schema } = useWebflowSchema();
  const { data: refs } = useWebflowRefs(!!configured);
  const rows = (schema?.rows || []).map((r) => ({ ...r, value: previewValue(r.key, story, refs) }));
  const missing = rows.filter((r) => r.required && !r.value && !AUTO_FILLED[r.key]);
  return (
    <div className="grid gap-5 lg:grid-cols-3">
      <section className="rounded-lg border border-border bg-card p-5 lg:col-span-1">
        <div className="kicker">Estado de sincronización</div>
        <div className="mt-3"><WebflowBadge status={story.webflowStatus} /></div>
        <dl className="mt-4 space-y-2 text-xs">
          <div className="flex justify-between gap-2"><dt className="text-muted-foreground">webflowItemId</dt><dd className="truncate font-mono" data-testid="webflow-item-id">{story.webflowItemId || "—"}</dd></div>
          <div className="flex justify-between gap-2"><dt className="text-muted-foreground">Última sincronización</dt><dd className="font-mono">{story.webflowLastSync ? fmtRel(story.webflowLastSync) : "—"}</dd></div>
        </dl>
        <p className="mt-4 text-[11.5px] leading-relaxed text-muted-foreground">El ID del item se guarda al primer envío: los siguientes envíos actualizan el mismo item en lugar de duplicarlo.</p>
        {!configured ? (
          <div className="mt-5 rounded-md border border-dashed border-border bg-surface-2 p-3 text-xs" data-testid="webflow-not-configured">
            Webflow aún no está conectado. {me.role === "SUPERADMIN" ? <Link to="/integraciones" className="underline">Configurar token y colección</Link> : "Un Superadmin debe configurar el token y la colección."}
          </div>
        ) : (
          canEdit && (
            <div className="mt-5 grid gap-2">
              {missing.length > 0 && (
                <div className="rounded-md border border-blush bg-blush/40 p-3 text-xs" data-testid="webflow-missing-fields">
                  Faltan campos obligatorios: <b>{missing.map((m) => m.label).join(", ")}</b>
                </div>
              )}
              <Button variant="outline" onClick={() => push.mutate(false)} disabled={push.isPending || missing.length > 0} data-testid="webflow-push-draft-button">
                {push.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <UploadCloud className="mr-1.5 h-4 w-4" />} Enviar a Webflow como borrador
              </Button>
              <Button onClick={() => setConfirm(true)} disabled={push.isPending || missing.length > 0} data-testid="webflow-publish-button"><Rocket className="mr-1.5 h-4 w-4" /> Publicar inmediatamente</Button>
              <Button variant="ghost" onClick={() => check.mutate()} disabled={!story.webflowItemId || check.isPending} data-testid="webflow-check-status-button">
                <RefreshCw className={cn("mr-1.5 h-4 w-4", check.isPending && "animate-spin")} /> Verificar estado
              </Button>
            </div>
          )
        )}
      </section>
      <section className="rounded-lg border border-border bg-card lg:col-span-2">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div className="text-sm font-semibold">Campos que se envían a «Artículos» ({rows.length})</div>
          <Pill tone={schema?.verified ? "mint" : "neutral"} data-testid="webflow-schema-status">{schema?.verified ? "Mapeo verificado con Webflow" : "Mapeo esperado · sin verificar"}</Pill>
        </div>
        <div className="divide-y divide-border" data-testid="webflow-field-rows">
          {rows.map((r) => (
            <div key={r.key} className="grid grid-cols-[200px_1fr] gap-3 px-5 py-2" data-testid={`webflow-row-${r.key}`}>
              <div className="min-w-0">
                <div className="text-[12.5px]">{r.label}{r.required && <span className="ml-0.5 text-bad">*</span>}</div>
                <div className="truncate font-mono text-[10px] text-muted-foreground">{r.slug}</div>
              </div>
              <div className={cn("truncate self-center text-[13px]", !r.value && (r.required && !AUTO_FILLED[r.key] ? "text-bad" : "text-muted-foreground"))}>
                {r.value || (AUTO_FILLED[r.key] && r.required ? AUTO_FILLED[r.key] : r.required ? "obligatorio · vacío" : "vacío (no se envía)")}
              </div>
            </div>
          ))}
        </div>
      </section>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Publicar en vivo?</AlertDialogTitle>
            <AlertDialogDescription>La historia quedará visible en el sitio de Webflow inmediatamente y pasará a la etapa «Publicada».</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => push.mutate(true)} data-testid="webflow-publish-confirm">Publicar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function StoryWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useMe();
  const canEdit = canAccess(me.role, "editStories");
  const { data: story, isLoading, error } = useQuery({ queryKey: ["story", id], queryFn: async () => (await api.get(`/stories/${id}`)).data });
  const { data: pub } = useQuery({ queryKey: ["settings-public"], queryFn: async () => (await api.get("/settings/public")).data });
  const { data: team = [] } = useQuery({ queryKey: ["team"], queryFn: async () => (await api.get("/team")).data });
  const [f, setF] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [tab, setTab] = useState("content");
  const [del, setDel] = useState(false);
  const dirtyRef = useRef(false);
  useEffect(() => { dirtyRef.current = dirty; }, [dirty]);
  useEffect(() => { if (story && !dirtyRef.current) setF(story); }, [story]);
  const set = (k, v) => { setF((x) => ({ ...x, [k]: v })); setDirty(true); };
  const onSaved = (s) => { qc.setQueryData(["story", id], s); qc.invalidateQueries({ queryKey: ["stories"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); };

  const createDoc = useMutation({
    mutationFn: async () => (await api.post(`/stories/${id}/google-doc`)).data,
    onSuccess: (s) => { onSaved(s); setF((prev) => ({ ...prev, googleDocsUrl: s.googleDocsUrl })); toast.success("Google Doc creado y vinculado"); window.open(s.googleDocsUrl, "_blank", "noopener,noreferrer"); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const importDoc = useMutation({
    mutationFn: async () => (await api.post(`/stories/${id}/google-doc/import`)).data,
    onSuccess: (r) => { setF((prev) => ({ ...prev, bodyText: r.text })); setDirty(true); toast.success("Manuscrito importado del Google Doc"); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const save = useMutation({
    mutationFn: async () => {
      const keys = ["title", "slug", "excerpt", "bodyText", "status", "format", "section", "authorName", "publishDate", "heroImageUrl", "seoTitle", "seoDescription", "googleDocsUrl", "canvaUrl", "printBookEligible", ...WF_STORY_KEYS];
      const body = Object.fromEntries(keys.map((k) => [k, f[k]]));
      return (await api.patch(`/stories/${id}`, body)).data;
    },
    onSuccess: (s) => { setDirty(false); setF(s); onSaved(s); toast.success("Cambios guardados"); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const remove = useMutation({
    mutationFn: async () => api.delete(`/stories/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["stories"] }); toast.success("Historia eliminada"); navigate("/editorial"); },
    onError: (e) => toast.error(errMsg(e)),
  });

  useEffect(() => {
    const h = (e) => { if ((e.metaKey || e.ctrlKey) && e.key === "s" && dirty && canEdit) { e.preventDefault(); save.mutate(); } };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  });

  if (error) return <div className="py-20 text-center text-sm text-muted-foreground">{errCode(error) || "Historia no encontrada"} · <Link to="/editorial" className="underline">Volver</Link></div>;
  if (isLoading || !f) return <div className="space-y-4"><Skeleton className="h-16 w-2/3" /><Skeleton className="h-[500px]" /></div>;
  const dis = !canEdit;
  const authorOptions = Array.from(new Set([f.authorName, ...team.map((t) => t.name)].filter(Boolean)));

  return (
    <div className="space-y-6" data-testid="story-workspace">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/editorial")} data-testid="story-back-button"><ArrowLeft className="mr-1.5 h-4 w-4" /> Mesa Editorial</Button>
        <div className="flex items-center gap-2">
          {story.isSample && <SampleTag />}
          <WebflowBadge status={story.webflowStatus} />
          {canEdit && (
            <>
              <Button variant="ghost" size="icon" onClick={() => setDel(true)} data-testid="story-delete-button" aria-label="Eliminar"><Trash2 className="h-4 w-4" /></Button>
              <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending} data-testid="story-save-button">
                {save.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} {dirty ? "Guardar cambios" : "Guardado"}
              </Button>
            </>
          )}
        </div>
      </div>
      <div className="border-b border-border pb-5">
        <div className="kicker mb-2">{f.section} · {FORMATS.find((x) => x.id === f.format)?.label}</div>
        <Textarea
          value={f.title}
          disabled={dis}
          onChange={(e) => set("title", e.target.value)}
          rows={2}
          className="min-h-0 resize-none border-0 bg-transparent p-0 font-display text-5xl leading-[0.95] tracking-tight shadow-none focus-visible:ring-0 md:text-6xl"
          data-testid="story-title-input"
        />
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="content" data-testid="story-tab-content">Contenido</TabsTrigger>
          <TabsTrigger value="social" data-testid="story-tab-social">Social Pack</TabsTrigger>
          <TabsTrigger value="webflow" data-testid="story-tab-webflow">Webflow</TabsTrigger>
        </TabsList>
        <TabsContent value="content" className="mt-5">
          <div className="grid gap-6 xl:grid-cols-[1fr_340px]">
            <div className="space-y-5">
              <Field label="Resumen / bajada" hint={<Counter value={f.excerpt} max={200} />}>
                <Textarea rows={2} value={f.excerpt} disabled={dis} onChange={(e) => set("excerpt", e.target.value)} data-testid="story-excerpt-input" />
              </Field>
              <Field label="Cuerpo del texto" hint={<span className="font-mono text-[10.5px] text-muted-foreground">{(f.bodyText || "").split(/\s+/).filter(Boolean).length} palabras · ## subtítulo · &gt; cita</span>}>
                <Textarea rows={18} value={f.bodyText} disabled={dis} onChange={(e) => set("bodyText", e.target.value)} className="font-[450] text-[14.5px] leading-relaxed" data-testid="story-body-input" />
              </Field>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <AssetLink label="Manuscrito en Google Docs" icon={FileText} value={f.googleDocsUrl} onChange={(v) => set("googleDocsUrl", v)} disabled={dis} embedFn={docsEmbed} testid="story-gdocs" placeholder="https://docs.google.com/document/d/…" />
                  {canEdit && (
                    <div className="flex flex-wrap gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => createDoc.mutate()} disabled={createDoc.isPending || !!f.googleDocsUrl} data-testid="story-gdoc-create-button">
                        {createDoc.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <FileText className="mr-1 h-3.5 w-3.5" />} Crear Google Doc
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => importDoc.mutate()} disabled={importDoc.isPending || !f.googleDocsUrl} data-testid="story-gdoc-import-button">
                        {importDoc.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1 h-3.5 w-3.5" />} Importar al cuerpo
                      </Button>
                    </div>
                  )}
                </div>
                <AssetLink label="Assets gráficos en Canva" icon={Palette} value={f.canvaUrl} onChange={(v) => set("canvaUrl", v)} disabled={dis} embedFn={canvaEmbed} testid="story-canva" placeholder="https://www.canva.com/design/…" />
              </div>
              <section className="rounded-lg border border-border bg-card p-4">
                <div className="kicker mb-3">SEO</div>
                <div className="grid gap-3">
                  <Field label="Meta título" hint={<Counter value={f.seoTitle} max={60} />}><Input value={f.seoTitle} disabled={dis} onChange={(e) => set("seoTitle", e.target.value)} data-testid="story-seo-title-input" /></Field>
                  <Field label="Meta descripción" hint={<Counter value={f.seoDescription} max={155} />}><Textarea rows={2} value={f.seoDescription} disabled={dis} onChange={(e) => set("seoDescription", e.target.value)} data-testid="story-seo-desc-input" /></Field>
                </div>
                <div className="mt-2 text-[11px] text-muted-foreground">Uso interno: la colección «Artículos» de Webflow no tiene campos SEO, así que estos no se envían.</div>
              </section>
              <WebflowExtraSections f={f} set={set} dis={dis} />
            </div>
            <aside className="space-y-4">
              <section className="space-y-3 rounded-lg border border-border bg-card p-4">
                <div className="kicker">Metadatos</div>
                <Field label="Etapa">
                  <Select value={f.status} onValueChange={(v) => set("status", v)} disabled={dis}>
                    <SelectTrigger data-testid="story-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{STORY_STAGES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Slug"><Input value={f.slug} disabled={dis} onChange={(e) => set("slug", e.target.value)} className="font-mono text-xs" data-testid="story-slug-input" /></Field>
                <Field label="Autor/a">
                  <Select value={f.authorName || ""} onValueChange={(v) => set("authorName", v)} disabled={dis}>
                    <SelectTrigger data-testid="story-author-select"><SelectValue placeholder="Asignar" /></SelectTrigger>
                    <SelectContent>{authorOptions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Sección">
                    <Select value={f.section} onValueChange={(v) => set("section", v)} disabled={dis}>
                      <SelectTrigger data-testid="story-section-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{Array.from(new Set([...SECTIONS, f.section].filter(Boolean))).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Publicación"><Input type="date" value={toDateInput(f.publishDate)} disabled={dis} onChange={(e) => set("publishDate", fromDateInput(e.target.value))} data-testid="story-date-input" /></Field>
                </div>
                <Field label="Formato">
                  <Select value={f.format} onValueChange={(v) => set("format", v)} disabled={dis}>
                    <SelectTrigger data-testid="story-format-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{FORMATS.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <label className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-xs">
                  <span className="flex items-center gap-1.5"><BookOpen className="h-3.5 w-3.5" /> Elegible para el Libro Anual</span>
                  <Switch checked={!!f.printBookEligible} disabled={dis} onCheckedChange={(v) => set("printBookEligible", v)} data-testid="story-print-switch" />
                </label>
                {story.printSpreadId && <div className="text-[11px] text-muted-foreground">Asignada a un pliego del <Link to="/flatplan" className="underline">Flatplan</Link>.</div>}
              </section>
              <WebflowMetaSection f={f} set={set} dis={dis} />
              <section className="space-y-2 rounded-lg border border-border bg-card p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="kicker">Foto de portada <span className="text-bad">*</span></div>
                  {canEdit && (
                    <ImageUploader
                      compact
                      disabled={dis}
                      testid="story-hero-uploader"
                      driveTitle="Elegir foto de portada en Drive"
                      onUploaded={(url) => set("heroImageUrl", url)}
                    />
                  )}
                </div>
                <Input value={f.heroImageUrl} disabled={dis} onChange={(e) => set("heroImageUrl", e.target.value)} placeholder="https://…/hero.jpg" className="font-mono text-[11px]" data-testid="story-hero-input" />
                {f.heroImageUrl ? <img src={f.heroImageUrl} alt="" className="aspect-[4/3] w-full rounded-md border border-border object-cover grayscale-[30%]" onError={(e) => (e.currentTarget.style.display = "none")} /> : <div className="flex aspect-[4/3] items-center justify-center rounded-md border border-dashed border-border bg-surface-2 text-xs text-muted-foreground">Sin imagen</div>}
                <p className="text-[10.5px] leading-snug text-muted-foreground">JPG, PNG o WebP · hasta 25 MB. Se guarda en Mediana Hub para que Webflow la acepte.</p>
              </section>
              <TasksWidget entity="story" entityId={id} title="Tareas de esta historia" />
            </aside>
          </div>
        </TabsContent>
        <TabsContent value="social" className="mt-5"><SocialPack story={story} canEdit={canEdit} pub={pub} onSaved={onSaved} /></TabsContent>
        <TabsContent value="webflow" className="mt-5">
          {dirty && <div className="mb-4 rounded-md border border-butter-strong bg-butter px-4 py-2 text-xs text-ink">Tienes cambios sin guardar: guárdalos antes de enviar a Webflow.</div>}
          <WebflowTab story={story} canEdit={canEdit && !dirty} pub={pub} me={me} onSaved={(s) => { onSaved(s); setF(s); }} />
        </TabsContent>
      </Tabs>
      <AlertDialog open={del} onOpenChange={setDel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta historia?</AlertDialogTitle>
            <AlertDialogDescription>Se quitará de la mesa editorial y de cualquier pliego del Flatplan. No elimina el item en Webflow.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => remove.mutate()} data-testid="story-delete-confirm">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
